import { deviceLocal } from '@/privacy/deviceStorage';
/**
 * Which branches of a work area's page tree this reader has opened (F-0201, AC-7).
 *
 * Per work area and per browser, because it is a reading position rather than content: the tree
 * itself is the work area's and the same for everyone, but where one person is reading in it is
 * theirs. `localStorage` is the store, so it survives a reload; the key carries the work area's id,
 * so opening a branch in one work area opens nothing in another.
 *
 * ⭐ **With no stored state every branch is collapsed** (Jay, 2026-09-18): a first visit, a new
 * browser and a work area never opened all draw only the top-level pages. A `localStorage` that
 * throws or is blocked — a private window, site data turned off — reads as exactly that, so a
 * reader there sees what a first-time reader sees rather than an error. Every access is wrapped:
 * nothing here throws, and nothing here is load-bearing for drawing the tree.
 */

const KEY_PREFIX = 'featit:pageTreeExpanded:';

/** The key for one work area. Keyed globally, two work areas would share one reading position. */
export function pageTreeExpansionKey(workAreaId: string): string {
  return `${KEY_PREFIX}${workAreaId}`;
}

function store(): Storage | null {
  try {
    // Reading the property is itself what throws in a browser with site data blocked.
    return typeof window === 'undefined' ? null : deviceLocal;
  } catch {
    return null;
  }
}

/** The ids of the branches this reader has open. Anything unreadable means: none of them. */
export function readExpandedPageIds(workAreaId: string): string[] {
  try {
    const raw = store()?.getItem(pageTreeExpansionKey(workAreaId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** Remembers the open branches. A store that refuses the write simply does not remember. */
export function writeExpandedPageIds(workAreaId: string, pageIds: readonly string[]): void {
  try {
    store()?.setItem(pageTreeExpansionKey(workAreaId), JSON.stringify([...pageIds]));
  } catch {
    // A full or read-only store costs this reader the memory of which branches were open, and
    // nothing else. It must never reach the screen as an error.
  }
}
